import { fetchUrlWithCookies } from 'contexts/user';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { mutate } from 'swr';
import styles from './LinkAccounts.module.css';
import { navigateTo } from 'lib/utils';
import * as analytics from 'lib/analytics';

type LinkAccountProps = {
  email: string;
  page: 'login' | 'signup';
  provider: string;
  sessionId?: string;
};

const LinkAccounts: FunctionComponent<LinkAccountProps> = ({
  email,
  page,
  provider,
  sessionId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const authSessionId = useMemo(
    () => sessionId ?? analytics.generateSessionId(),
    [sessionId]
  );
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current) {
      return;
    }

    shownRef.current = true;
    analytics.authLinkingAccountsShown({
      session_id: authSessionId,
      page,
      provider,
    });
  }, [authSessionId, page, provider]);

  const onLink = async () => {
    if (isLoading) {
      return;
    }
    analytics.authLinkAccountClicked({
      session_id: authSessionId,
      page,
      provider,
    });
    setIsLoading(true);

    await fetchUrlWithCookies('/api/linkAccount', {
      redirect: 'manual',
    });
    navigateTo('/');
  };

  const onCancel = async () => {
    if (isLoading) {
      return;
    }

    analytics.authLinkCancelClicked({
      session_id: authSessionId,
      page,
      provider,
    });
    setIsLoading(true);
    const canceled = await fetchUrlWithCookies('/api/cancelLink');
    const success = canceled.status;
    if (success === 200) {
      mutate('/api/me', { signedIn: false });
    }
    setIsLoading(false);
    navigateTo('/login');

    return;
  };

  return (
    <div className={styles.LoginLinkText}>
      <p>
        {'A rive account associated with '}
        <span className={styles.LoginLinkHighlight}>{email}</span>{' '}
        {' already exists. Do you want to link it with your '}
        <span className={styles.LoginLinkHighlight}>
          {provider.charAt(0).toUpperCase() + provider.slice(1)}
        </span>{' '}
        account?
      </p>
      <div className={styles.SocialLogin}>
        <div
          onClick={onLink}
          className={!isLoading ? styles.LoginLink : styles.LoginLinkDisabled}
        >
          Link Account
        </div>
        <div
          onClick={onCancel}
          className={
            !isLoading ? styles.LoginLinkCancel : styles.LoginLinkCancelDisabled
          }
        >
          Cancel
        </div>
      </div>
    </div>
  );
};

export default LinkAccounts;
